"use client"

import React, { RefObject, useEffect, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import Marker from "./components/Marker"
import { Flex } from "@chakra-ui/react"
import { Button, Map, YMaps } from "@pbe/react-yandex-maps"

import { useTranslations } from "next-intl"
import { useAddressStore } from "@/entities/address/model/use-address-store"
import { apikeyService, yandexApi } from "@/entities/address/api/client"
import { getUserLocation } from "@/features/auto-geolocate/lib"
import "./styles.css"

interface IProps {
  mapRef: RefObject<any>
  isMobile?: boolean
  geometry: number[]
  outerLoading: boolean
  onMapBounded: (e: number[]) => void
  onLocationChange: (e: any, isSelect: boolean, findLocation?: boolean) => void
}

export const countryGeometries: { [key: string]: [number, number] } = {
  UZ: [41.31119825, 69.27975936],
  KZ: [51.169392, 71.449074],
}

const YandexMap: React.FC<IProps> = ({
  isMobile = true,
  outerLoading = false,
  mapRef = null,
  geometry = countryGeometries["UZ"],
  onLocationChange = () => {},
  onMapBounded = () => {},
}) => {
  const [innerLoading, setInnerLoading] = useState(false)
  const [zoom, setZoom] = useState(17)
  const [isLocation, setIsLocation] = useState(false)
  const [isPermission, setIsPermission] = useState(false)
  const [ymaps, setYmaps] = useState<any>(null)
  const [isDragging, setDragging] = useState(false)
  const [apikey, setApikey] = useState("")
  const [determiningLocation, setDeterminingLocation] = useState(false)

  const t = useTranslations()

  const map = useAddressStore((state) => state.map)

  const { data: apiKeysData, refetch } = useQuery({
    queryKey: ["api-keys"],
    queryFn: () =>
      apikeyService.getList({ page: 1, limit: 10, type: "geocoder" }),

    enabled: true,
  })

  useEffect(() => {
    if (apiKeysData && apiKeysData?.data?.ids?.length > 0) {
      setApikey(apiKeysData?.data?.ids[0])
    }
  }, [apiKeysData])

  const onBoundsChange = (e: any) => {
    const coords = e.originalEvent.newCenter
    setDragging(false)
    setInnerLoading(true)
    getAddress(coords)
    onMapBounded(coords)
  }

  const getAddress = async (coords: number[]) => {
    try {
      const response = await yandexApi.getGeocode({
        geocode: coords.join(","),
        lang: "ru_RU",
      })

      const geoObject =
        response?.data?.response?.response?.GeoObjectCollection
          ?.featureMember?.[0]?.GeoObject

      if (geoObject) {
        const components =
          geoObject.metaDataProperty?.GeocoderMetaData?.Address?.Components ||
          []
        const values: Record<string, string> = {}
        let city = ""
        let house = ""

        components.forEach(({ kind, name }: { kind: string; name: string }) => {
          if (kind === "house") {
            house = name
          }
          if (["locality", "province"].includes(kind)) {
            city = name
          } else if (
            [
              "district",
              "street",
              "area",
              "house",
              "other",
              "vegetation",
            ].includes(kind)
          ) {
            values[kind] = name
          }
        })

        const formattedAddress = Object.values(values).join(", ") || city || ""

        onLocationChange(
          {
            address: formattedAddress,
            city,
            house,
            location: coords,
          },
          false
        )
      }
    } catch (error: any) {
      const { status, data } = error || {}
      if (
        [403, 429].includes(status) ||
        data?.message === "Limit is exceeded"
      ) {
        refetch()
      }
      console.error("Failed to fetch address:", error)
    } finally {
      setInnerLoading(false)
    }
  }

  const onGeolocate = () => {
    setDeterminingLocation(true)
    getUserLocation()
      .then((location) => {
        setIsLocation(false)
        setIsPermission(false)
        mapRef?.current?.setCenter(
          [location?.latitude, location?.longitude],
          17,
          {
            duration: 300,
          }
        )
        location && getAddress([location?.latitude, location?.longitude])
      })
      .catch(() => {
        setIsLocation(true)
        setIsPermission(true)
        setTimeout(() => {
          setIsLocation(false)
        }, 5000)
      })
      .finally(() => {
        setDeterminingLocation(false)
      })
  }

  useEffect(() => {
    if (map && mapRef?.current) {
      onGeolocate()
    }
  }, [map, mapRef?.current])

  const geolocationLayout = () => {
    if (ymaps) {
      return ymaps?.templateLayoutFactory?.createClass(
        `<div class=${"geolocationControl"}>
        <svg
        width='18'
        height='18'
        viewBox='0 0 18 18'
        xmlns='http://www.w3.org/2000/svg'
      >
        <mask
          id='mask0_1138_50368'
          style={{ maskType: 'alpha' }}
          maskUnits='userSpaceOnUse'
          x='0'
          y='0'
          width='18'
          height='18'
        >
          <rect width='18' height='18' fill='#D9D9D9' />
        </mask>
        <g mask='url(#mask0_1138_50368)'
        >
          <path
            d='M7.53789 10.4623L2.68164 8.49356C2.51914 8.43105 2.40039 8.33418 2.32539 8.20293C2.25039 8.07168 2.21289 7.9373 2.21289 7.7998C2.21289 7.6623 2.25352 7.52793 2.33477 7.39668C2.41602 7.26543 2.53789 7.16855 2.70039 7.10605L14.2129 2.83105C14.3629 2.76855 14.5066 2.75605 14.6441 2.79355C14.7816 2.83105 14.9004 2.8998 15.0004 2.9998C15.1004 3.0998 15.1691 3.21855 15.2066 3.35605C15.2441 3.49355 15.2316 3.6373 15.1691 3.7873L10.8941 15.2998C10.8316 15.4623 10.7348 15.5842 10.6035 15.6654C10.4723 15.7467 10.3379 15.7873 10.2004 15.7873C10.0629 15.7873 9.92852 15.7498 9.79727 15.6748C9.66602 15.5998 9.56914 15.4811 9.50664 15.3186L7.53789 10.4623Z'
            fill="inherit"
          />
        </g>
      </svg>
        ${t("location_determination")}</div>`
      )
    }

    return undefined
  }

  if (!apikey) return null

  return (
    <Flex
      flex="1"
      zIndex={0}
      flexDir="column"
      overflow="hidden"
      position="relative"
      className="wrapper"
    >
      <Marker
        hover={isDragging}
        isLocation={isLocation}
        isLoading={outerLoading || innerLoading}
      />
      <YMaps query={{ apikey }}>
        <Map
          state={{
            center: geometry,
            zoom,
          }}
          onLoad={(e) => {
            setYmaps(e)
          }}
          instanceRef={mapRef || undefined}
          onMouseDown={() => setDragging(true)}
          onBoundsChange={onBoundsChange}
          modules={["Placemark", "geocode", "Layer", "templateLayoutFactory"]}
          className={"mmap"}
          options={{
            copyrightLogoVisible: false,
            copyrightProvidersVisible: false,
            copyrightUaVisible: false,
            avoidFractionalZoom: true,
            suppressMapOpenBlock: true,
          }}
        >
          <Button
            options={{
              layout: geolocationLayout(),
              position: { bottom: isMobile ? "70px" : "20px", left: "50%" },
            }}
            state={{ enabled: !isPermission }}
            data={{ content: "Gelocation" }}
            defaultState={{ selected: true }}
            onClick={onGeolocate}
            disabled={determiningLocation}
          />
        </Map>
      </YMaps>
    </Flex>
  )
}
function errorHandler(err: any) {
  if (err.code === 1) {
    console.log("Error: Access is denied!")
  } else if (err.code === 2) {
    console.log("Error: Position is unavailable!")
  }
}
export default YandexMap
